'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { ChevronDown, Sparkles, X } from 'lucide-react';
import React, { useState, useCallback, useEffect } from 'react';

import { useAIPlannerSheet } from './ai-planner-context';
import { useGetMealPlan } from '@/api/use-get-meal-plan';
import { useNavigation } from '@/components/navigation/navigation-context';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';
import { BANNER_DISMISS_KEY } from '@/store/ai-planner';

interface AIPlannerBannerProps {
  className?: string;
}

// Check if banner was dismissed in this session
function wasDismissedThisSession(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return sessionStorage.getItem(BANNER_DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

// Mark banner as dismissed for this session
function dismissForSession(): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(BANNER_DISMISS_KEY, 'true');
  } catch {
    // ignore
  }
}

export function AIPlannerBanner({ className }: AIPlannerBannerProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const { isAuthenticated, user } = useAuthStore();

  const { openSheet } = useAIPlannerSheet();

  const { setForceHidden } = useNavigation();

  // Fetch meal plan to check if user has existing plan
  // React Query will share cache with ai-planner-context.tsx (same query key)
  // Only fetch when authenticated and not yet dismissed
  const { data: mealPlanData, isLoading } = useGetMealPlan(
    user?.systemToken ?? '',
    {
      enabled: isAuthenticated && !wasDismissedThisSession(),
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Check if user has an active meal plan
  const hasPlan = Boolean(mealPlanData?.planning_table?.length);

  // Check visibility based on API data
  useEffect(() => {
    // Hide if not authenticated
    if (!isAuthenticated) {
      setIsDrawerOpen(false);
      return;
    }

    // Hide while loading meal plan data
    if (isLoading) {
      setIsDrawerOpen(false);
      return;
    }

    // Hide if user already has a meal plan
    if (hasPlan) {
      setIsDrawerOpen(false);
      return;
    }

    // Hide if dismissed in this session
    if (wasDismissedThisSession()) {
      setIsDrawerOpen(false);
      return;
    }

    // Show drawer after a short delay (only for users without a plan)
    const timer = setTimeout(() => {
      setIsDrawerOpen(true);
      setForceHidden(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [isAuthenticated, isLoading, hasPlan, setForceHidden]);

  const handleDismiss = useCallback(() => {
    trackEvent('meal_plan', {
      button_name: 'meal_plan_popup_back',
      button_location: 'home_page',
      event_name: 'click_button',
    });
    dismissForSession();
    setForceHidden(false);
    setIsDrawerOpen(false);
  }, [setForceHidden]);

  const handleMinimize = useCallback(() => {
    setIsDrawerOpen(false);
    setForceHidden(false);
    setIsMinimized(true);
  }, [setForceHidden]);

  const handleOpenMinimized = useCallback(() => {
    setIsDrawerOpen(true);
    setForceHidden(true);
    setIsMinimized(false);
  }, [setForceHidden]);

  const handleOpenSheet = useCallback(() => {
    trackEvent('meal_plan', {
      button_name: 'meal_plan_popup',
      button_location: 'home_page',
      event_name: 'click_button',
    });
    openSheet();
    setIsDrawerOpen(false);
    setIsMinimized(false);
  }, [openSheet]);

  return (
    <>
      {/* Minimized FAB Button */}
      {isMinimized && (
        <button
          onClick={handleOpenMinimized}
          className={cn(
            'fixed bottom-20 right-4 z-50',
            'flex items-center gap-2 px-4 py-3 rounded-full',
            'bg-gradient-to-r from-[#ff8200] to-[#ff6b00]',
            'text-white font-medium text-sm shadow-lg',
            'hover:scale-105 transition-transform',
            'animate-in slide-in-from-bottom-8 fade-in-0 duration-500',
            className
          )}
        >
          <Sparkles className="size-4" />
          AI lên lịch ăn
          <ChevronDown className="size-4 rotate-180" />
        </button>
      )}

      {/* Drawer Sheet */}
      <Sheet
        open={isDrawerOpen}
        onOpenChange={(open) => {
          setIsDrawerOpen(open);
          if (!open) {
            setForceHidden(false);
          }
        }}
      >
        <SheetContent
          side="bottom"
          className="h-auto max-h-[85vh] p-0 bg-white rounded-t-3xl border-0"
        >
          <SheetTitle className="hidden">
            AI lập kế hoạch ăn trưa tự động
          </SheetTitle>

          {/* Drawer Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 pb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold">
                AI lập kế hoạch ăn trưa tự động
              </h2>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-full hover:bg-neutral-10 text-neutral-50 hover:text-neutral-70 transition-colors"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 pb-6">
            {/* Promotional Banner */}
            <div className="relative w-full aspect-[344/148] shadow-sm mb-6 rounded-md overflow-hidden">
              <Image
                src={`${assetPrefix}/static/images/ai-planner-banner.webp`}
                alt="AI Planner"
                fill
                className="w-full h-auto object-contain"
              />
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleOpenSheet}
              className="w-full bg-gradient-to-r from-[#ff8200] to-[#ff6b00] hover:from-[#ff8200]/90 hover:to-[#ff6b00]/90 text-white font-semibold rounded-xl h-12 text-base shadow-lg"
            >
              Lên lịch bữa trưa cho tuần này
            </Button>

            {/* Minimize Link */}
            <Button
              variant="secondary"
              onClick={handleMinimize}
              className="w-full mt-3 text-center text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              Thu gọn
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// Simple trigger button for testing/demo
export function AIPlannerTriggerButton({ className }: { className?: string }) {
  const { openSheet } = useAIPlannerSheet();

  const handleOpenSheet = () => {
    trackEvent('meal_plan', {
      button_name: 'meal_plan_popup',
      button_location: 'home_page',
      event_name: 'click_button',
    });
    openSheet();
  };

  return (
    <Button
      onClick={handleOpenSheet}
      className={cn(
        'bg-gradient-to-r from-[#ff8200] to-[#ff6b00] hover:from-[#ff8200]/90 hover:to-[#ff6b00]/90',
        'text-white font-semibold rounded-full shadow-lg',
        'flex items-center gap-2',
        className
      )}
    >
      <Sparkles className="size-4" />
      AI lên lịch ăn trưa
    </Button>
  );
}
